<template>
  <div class>
    <ModalBox v-model="legacyModalVisible" size="md" hide-close header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none">

      <div class="p-6 h-[480px] max-h-[80vh] flex flex-col gap-y-5">
        <!-- Header -->
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="text-lg font-semibold text-white mb-0.5">Guest destinations</p>
            <p class="text-tiny text-surface-7 mb-0">Destinations contributed by guests through your invite link.</p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <ButtonCopyToClipboard label="Copy invite link" withText :text="this.link">
              <IconBase class="mr-1" fill="none">
                <IconShare />
              </IconBase>
              Copy invite link
            </ButtonCopyToClipboard>
            <Tooltip :active=true content="Manage who can use your invite link">
              <FormButton type="secondary" size="sm" @click="onClickManageAccessPopup">
                Manage access
              </FormButton>
            </Tooltip>
          </div>
        </div>

        <!-- Filter -->
        <div class="flex items-center gap-2">
          <span class="text-tiny text-surface-7 shrink-0">Added by</span>
          <DropDownMenu ref="addedByDropdown" type="secondary"
            customClasses="bg-surface-3 hover:bg-surface-4 rounded-md flex justify-between items-center gap-2 h-8 text-tiny !px-3 min-w-[180px] transition-colors">
            <template #toggle-button>
              <span class="truncate">{{ selectedLabel || 'All users' }}</span>
              <icon-base class="text-surface-7 shrink-0">
                <icon-chevron-down />
              </icon-base>
            </template>
            <template #menu-items>
              <DropDownMenuItem v-if="filteredPlatforms.length === 0">
                No user destinations
              </DropDownMenuItem>
              <DropDownMenuItem @click="handleUserSelect('All users')">
                All users
              </DropDownMenuItem>
              <DropDownMenuItem v-for="user in uniqueUsers" :key="user" @click="handleUserSelect(user)">
                {{ user.name }} ({{ user.email }})
              </DropDownMenuItem>
            </template>
          </DropDownMenu>
        </div>

        <InviteLinkPage v-if="isModalOpen" @saveInfo="handleSaveInfo" />

        <!-- Loading -->
        <div v-if="processing" class="flex-1 flex justify-center items-center" aria-busy="true">
          <Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
        </div>

        <!-- List -->
        <div v-else-if="platforms.length" class="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
          <ul class="bg-surface-1 rounded-xl p-2 flex flex-col gap-y-1">
            <li v-for="(plat, index) in filteredPlatforms" :key="index"
              class="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-2 transition-colors">
              <div class="shrink-0 size-10 rounded-lg bg-surface-3 flex items-center justify-center overflow-hidden">
                <img v-if="!plat.icon || plat.template === 'custom'" src="@/assets/icons/cog.svg"
                  class="w-5 h-5 object-contain opacity-80" />
                <img v-else :src="getIcon(plat.icon)" class="w-6 h-6 object-contain" />
              </div>
              <div class="flex flex-col min-w-0 grow">
                <span class="text-sm font-medium text-white truncate">{{ plat.name || 'Custom' }}</span>
                <span class="text-tiny text-surface-7 truncate">Added by {{ plat.ownerName }}</span>
              </div>
              <Tooltip v-if="isAdded(plat) || reachedPlatformLimit"
                :content="isAdded(plat) ? 'Destination already added' : 'You cannot add any more destinations'">
                <span v-if="isAdded(plat)"
                  class="inline-flex items-center gap-1.5 rounded-md bg-success-transparent text-success text-tiny font-medium px-3 h-8 shrink-0">
                  <icon-base width="14" height="14" fill="none">
                    <icon-checkmark-stroke />
                  </icon-base>
                  Added
                </span>
                <FormButton v-else type="secondary" size="sm" disabled class="shrink-0">
                  <icon-base class="mr-1.5" fill="none">
                    <icon-plus />
                  </icon-base>
                  Add
                </FormButton>
              </Tooltip>
              <FormButton v-else type="primary" size="sm" class="shrink-0" :disabled="disableAllBtns"
                @click="addPlatform(plat)">
                <icon-base class="mr-1.5" fill="none">
                  <icon-plus />
                </icon-base>
                Add
              </FormButton>
            </li>
          </ul>
        </div>

        <!-- Empty -->
        <div v-else class="flex-1 flex justify-center items-center flex-col text-center px-6">
          <img src="@/assets/icons/noGuestDes.svg" alt="no-destinations" class="opacity-90">
          <p class="text-sm font-medium text-surface-8 mt-4 mb-1">No guest destinations yet</p>
          <p class="text-tiny text-surface-6 mb-0">Share your stream link with a guest — destinations they add will show up
            here.</p>
        </div>

        <!-- Footer -->
        <div class="mt-auto pt-4 flex items-center justify-between gap-3">
          <div class="flex flex-col gap-y-0.5">
            <p class="text-tiny text-white m-0">
              You've used
              <span class="font-medium" :class="destinationCountText.isMax ? 'text-danger' : ''">{{
                destinationCountText.count }}</span>
              destinations
            </p>
            <span class="text-tiny text-surface-7 m-0">Need more? <a class="underline hover:text-white"
                target="blank" href="/app/subscribe">Upgrade</a> here.</span>
          </div>
          <FormButton type="secondary" size="sm" @click="dismiss">
            Close
          </FormButton>
        </div>
      </div>
    
</ModalBox>
  </div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import _ from "lodash";
import ClipLoader from "@/components/ui/ClipLoader.vue";
import FormButton from "../Atoms/FormButton.vue";
import Spinner from "@/components/ui/Spinner.vue";
import GuestDestinationService from "../../services/GuestDestinationService";
import StreamService from '@/services/StreamService';
import IconBase from '@/components/icon/IconBase.vue'
import IconPlus from '@/components/icon/IconPlus.vue';
import IconCheckmarkStroke from '@/components/icon/IconCheckmarkStroke.vue';
import InviteLinkPage from "./InviteLinkPage.vue";
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import DropDownMenu from '../Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue';
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue';
import IconShare from '@/components/icon/IconShare.vue';

export default {
  name: "GuestDestinationPopup",
  mixins: [legacyModalBridgeMixin],
  legacyModalId: 'select-guest-destinations-modal',
  components: {
		ModalBox,
    ClipLoader,
    FormButton,
    Spinner,
    IconBase,
    IconPlus,
    IconCheckmarkStroke,
    InviteLinkPage,
    Tooltip,
    DropDownMenu,
    DropDownMenuItem,
    IconChevronDown,
    ButtonCopyToClipboard,
    IconShare,
  },
  props: {
    disabledPlatformIds: Array,
    stream: {
      type: Object,
    },
    modalSize: {
      type: String,
      default: "sm",
      validator(v) {
        return ["xl", "lg", "md", "sm"].some((size) => size === v);
      },
    },
    platformUsage: {
      type: Object,
    }
  },

  data() {
    return {
      data: [],
      platforms: [],
      processing: true,
      disableButtons: [],
      addedKeys: [],
      isOpen: false,
      selectedUser: '',
      selectedLabel: 'All users',
      isModalOpen: false,
      reachedPlatformLimit: false,
      disableAllBtns: false,
    }
  },
  computed: {
    guestDestinationCount() {
      const liveSub = _.find(this.$store.state.User.addonSub, { category: 'live', enabled: true });
      if (!liveSub) return false;
      if ('maxGuestDestinations' in liveSub.definitionOverride) {
        return liveSub.definitionOverride.maxGuestDestinations;
      }
      return liveSub?.package?.definition?.maxGuestDestinations;
    },
    addedPlatformsCount() {
      return this.stream?.platforms?.filter(p => p.isGuest)?.length || 0;
    },
    destinationCountText() {
      const current = this.platformUsage.usedDestination
      const max = this.platformUsage.totalDestination
      const isMax = current >= max

      return {
        count: `${current}/${max}`,
        isMax
      }
    },
    link() {
      return `${location.origin}/app/invite-link/${this.stream._id}`
    },
    filteredPlatforms() {
      if (!this.selectedUser || this.selectedUser.name === 'All users') {
        return this.platforms;
      }

      return this.platforms.filter(p =>
        p.ownerEmail?.trim().toLowerCase() === this.selectedUser.email.trim().toLowerCase()
      );
    },
    uniqueUsers() {
      const seen = new Set();
      const users = [];

      for (const p of this.platforms) {
        const name = p.ownerName;
        const email = p.ownerEmail;

        if (typeof name === 'string') {
          const key = `${name} (${email})`;
          if (!seen.has(key)) {
            seen.add(key);
            users.push({ name, email });
          }
        }
      }

      // users.unshift({ name: 'All users', email: '' });
      return users;
    }
  },
  mounted() {
  },
  methods: {
    getIcon(icon) {
      icon = icon.replace('png', 'svg');
      try {
        return require('@/assets/images/platforms/' + icon);
      } catch (err) {
        return require('@/assets/images/platforms/custom.svg');
      }
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    handleUserSelect(user) {
      if (user === 'All users') {
        this.selectedUser = null
        this.selectedLabel = 'All users'
      } else {
        this.selectedUser = user
        this.selectedLabel = `${user.name} (${user.email})`
      }
      this.isOpen = false
      this.$refs.addedByDropdown?.hideDropDownMenu()
    },
    platformKey(plat) {
      return `${plat?.server || ''}|${plat?.key || ''}`
    },
    isAdded(plat) {
      return this.addedKeys.includes(this.platformKey(plat)) || this.isAlreadyAdded(plat)
    },
    handleSaveInfo(info) {
      this.selectedUser = info
    },
    isAlreadyAdded(plat) {
      return this.stream?.platforms?.some(
        p => p.server === plat.server && p.key === plat.key
      );
    },
    isDisabled(plat) {
      if (this.reachedPlatformLimit) return true;

      for (let i = 0; i < this.stream?.platforms?.length; i++) {
        if (this.stream?.platforms?.[i].server === plat.server && this.stream?.platforms?.[i].key === plat.key) return true;
      }

      return false;
    },
    async addPlatform(payload) {
      if (this.disableAllBtns) return;
      this.disableAllBtns = true;

      const totalDestinationLimitReached = this.platformUsage?.usedDestination && (this.platformUsage.usedDestination + 1) > this.platformUsage.totalDestination;
      if (totalDestinationLimitReached) {
        this.reachedPlatformLimit = true;
        this.$notify({ group: 'error', text: `You have reached max allowed destination count(${this.guestDestinationCount})` })
        this.disableAllBtns = false;
        return;
      }

      // Only the API call decides success/failure of the add itself.
      let platform;
      try {
        payload.isGuest = true;
        platform = await StreamService.addStreamPlatform(this.stream._id, payload);
      } catch (e) {
        console.error('addStreamPlatform failed', e);
        this.$notify({ group: 'error', text: 'Could not add destination. Please try again.' });
        this.disableAllBtns = false;
        return;
      }

      // Add succeeded — reflect it locally.
      this.platformUsage.usedDestination++;
      this.addedKeys.push(this.platformKey(payload));
      this.$notify({ group: 'success', text: 'Destination added.' });
      if ((this.platformUsage.usedDestination) === this.platformUsage.totalDestination) {
        this.reachedPlatformLimit = true;
      }

      // Notify parent separately so a downstream handler error doesn't look like an add failure.
      try {
        this.$emit('platform-saved', platform, this.selectedGroup);
        this.$emit('destinations-changed');
      } catch (e) {
        console.error('post-add handler error', e);
      }

      this.disableAllBtns = false;
    },
    onRemovedGuest(removedEmail = null) {
      this.platforms = [];
      this.data = this.data.filter(x => x.email !== removedEmail);
      this.$emit('refresh-guest-destinations', this.data);
      this.data.forEach(x => {
        this.platforms.push(...this.addAndAuthorIcons(x.destinations || [], x))
      });
    },
    async fetchDestinations() {
      this.data = await GuestDestinationService.getDestinations(this.stream._id);
      this.$emit('refresh-guest-destinations', this.data);
      this.data.forEach(x => {
        this.platforms.push(...this.addAndAuthorIcons(x.destinations || [], x))
      });
    },
    addAndAuthorIcons(destinations, session) {
      return destinations.map(dest => {
        dest.icon = `${this.getPlatformIcon(dest.template || dest.name)}`;
        dest.ownerEmail = session.email;
        dest.ownerName = session.name;
        return dest;
      })
    },

    getPlatformIcon(name) {
      const icon = GuestDestinationService.getPlatformIcon(name);
      return icon
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.link);
      this.$notify({ group: 'info', text: 'Copied to clipboard' });
    },
    onClickGuestStreamLinkPopup() {
      showModal("modal-stream-link");
    },
    onClickManageAccessPopup() {
      showModal("modal-manage-access");
    },
    onLegacyModalShow() {
      this.onShown();
    },
    async onShown() {
      this.processing = true;
      this.platforms = [];
      this.addedKeys = [];
      this.selectedUser = null;
      this.selectedLabel = 'All users';
      await this.fetchDestinations();

      const totalDestinationLimitReached = this.platformUsage?.usedDestination && (this.platformUsage.usedDestination + 1) > this.platformUsage.totalDestination;

      if (totalDestinationLimitReached) {
        this.reachedPlatformLimit = true;
      } else {
        this.reachedPlatformLimit = false;
      }
      this.disableButtons = this.platforms.map(() => false)
      this.processing = false;
    },
    dismiss() {
      this.hideLegacyModal();
    },
  },
};
</script>

<style>
#select-guest-destinations-modal .modal-sm {
  max-width: 700px;
  padding: 0px 10px;
}

#select-guest-destinations-modal .modal-body .col {
  max-height: 80vh;
  overflow-y: auto;
}

#select-guest-destinations-modal .modal-sm .grid-item {
  width: 600px;
  max-width: 100%;
  flex: 0 0 100%;
}

#select-guest-destinations-modal .platform_details_wrapper {
  display: none;
}

#select-guest-destinations-modal_BV_modalbody ul {
  border-bottom: 1px solid var(--c-dark-3);
}

#select-guest-destinations-modal_BV_modalbody ul li a {
  background: transparent;
  border: 0px;
  color: var(--c-dark-8);
  font-weight: 400;
  font-size: 15px;
}

div#select-guest-destinations-modal___BV_modalbody ul li a.active {
  font-weight: 500;
  color: #fff;
  border-bottom: 2px solid #fff;
}
</style>

<style scroped>
.seach-icon {
  transform: translateX(0);
  background: transparent;
  border: none;
  color: rgba(173, 184, 220, 0.5);
}

.no-data-err {
  height: 25vh;
  display: flex;
  justify-content: center;
  align-items: center;

  text-align: center;
}

.no-data-err h3 {
  color: #fff;
  font-size: var(--font-s-s);
  font-weight: 500;
}

.no-data-err .err-description {
  color: rgba(135, 148, 181, 1);
  font-size: 13px;
  font-weight: 400;
}

@media only screen and (max-width: 600px) {
  .tab-vod .inner_folder_wrap>p:last-child {
    display: none !important;
  }

  .tab-vod .inner_folder_wrap>p {
    width: 50%;
  }

  .tab-livestream .inner_folder_wrap>div {
    width: 100%;
  }

  .files_parent_al {
    height: unset;
    padding: 10px 0px;
  }

  .files_parent_al .al_badge+span {
    padding-left: 38px;
  }

  #select-guest-destinations-modal .loading-showcase {
    padding: 0;
  }

  #select-guest-destinations-modal .modal-body {
    padding-left: 15px;
    padding-right: 15px;
  }

  #select-guest-destinations-modal .modal-body .col {
    overflow-x: hidden;
  }

  .tab-vod .files_parent_al>img:first-child {
    margin-right: 5px !important;
  }

  #select-guest-destinations-modal .sw_modal_action {
    padding: 0;
  }

  #select-guest-destinations-modal .sw_modal_action .btn-outline-danger {
    margin-right: 0 !important;
  }

  #select-guest-destinations-modal .sw_modal_action button {
    margin-bottom: 10px;
    width: 48%;
  }
}
</style>
